"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Share2 } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-12 w-3/4 rounded-lg bg-muted/30 animate-pulse mb-6" />
        <div className="h-96 w-full rounded-xl bg-muted/30 animate-pulse mb-8" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted/30 animate-pulse" />
          <div className="h-4 w-full rounded bg-muted/30 animate-pulse" />
          <div className="h-4 w-3/4 rounded bg-muted/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Failed to load post content.</p>
      </div>
    );
  }

  const post = posts[0];
  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-all duration-[150ms]">
          <ArrowLeft className="size-4" />
          Back to Home
        </Link>
      </div>

      <header className="mb-8">
        <h1 className="text-4xl font-display font-bold text-foreground mb-6 leading-tight lg:text-5xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>{formattedDate}</time>
            </div>
          )}

          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.categories.map((category) => (
              <span
                key={category.id}
                className="inline-flex items-center rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </header>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      <div
        className="prose prose-lg max-w-none mb-12 text-foreground prose-headings:font-display prose-headings:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:text-foreground prose-code:bg-muted/30 prose-code:rounded prose-code:px-1 prose-code:py-0.5"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-10" />

      {post.author && (
        <section className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">About the Author</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                <Avatar className="size-16">
                  {post.author.avatar && post.author.avatar["96"] ? (
                    <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                  ) : null}
                  <AvatarFallback className="text-lg font-semibold">
                    {post.author.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-lg mb-1">
                    {post.author.name}
                  </h3>
                  {post.author.link && (
                    <Link
                      to={post.author.link}
                      className="text-sm text-primary hover:underline"
                    >
                      View all posts →
                    </Link>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {post.tags && post.tags.length > 0 && (
        <section className="mb-12">
          <h3 className="font-display font-semibold text-lg mb-4">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center rounded-md bg-secondary/20 px-3 py-1.5 text-sm text-secondary-foreground"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mb-12">
        <Card>
          <CardContent className="flex items-center justify-between py-6">
            <span className="font-display font-semibold text-lg">Share this post</span>
            <Button variant="outline" size="sm">
              <Share2 className="size-4" />
              Share
            </Button>
          </CardContent>
        </Card>
      </section>

      <PostsProvider postType="post" wp_query={{ per_page: 3, order: "desc", orderby: "date", exclude: [post.id] }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <section>
        <h2 className="font-display font-bold text-2xl mb-6">Related Posts</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 rounded-xl bg-muted/30 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section>
      <h2 className="font-display font-bold text-2xl mb-6">Related Posts</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {posts.map((relatedPost) => (
          <Card key={relatedPost.id} className="overflow-hidden hover:-translate-y-1 transition-all duration-[150ms]">
            {relatedPost.featuredImage && (
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={relatedPost.featuredImage}
                  alt={relatedPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <CardContent className="pt-6">
              <h3 className="font-display font-semibold text-lg mb-3 line-clamp-2">
                {relatedPost.title}
              </h3>
              {relatedPost.excerpt && (
                <p
                  className="text-sm text-muted-foreground mb-4 line-clamp-3"
                  dangerouslySetInnerHTML={{ __html: relatedPost.excerpt }}
                />
              )}
              {relatedPost.link && (
                <Link
                  to={relatedPost.link}
                  className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  Read more →
                </Link>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = typeof window !== "undefined" && (window as any).wvcClient
    ? (window as any).wvcClient.getCurrentPostID()
    : null;

  if (!currentPostId) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Unable to load post.</p>
      </div>
    );
  }

  return (
    <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
      <SinglePostContent />
    </PostsProvider>
  );
}